/**
 * Dashboard component is the main entry point for authenticated users.
 * It manages feature flags, authentication, language, account data, and renders the dashboard layout and modals.
 *
 * Key responsibilities:
 * - Handles authentication and token state
 * - Loads user account and consent data
 * - Integrates with analytics and A/B testing tools
 * - Manages language and dynamic content
 * - Renders dashboard routes and modals
 *
 * @module Dashboard
 */
import { ReactElement, Suspense, useState } from 'react'
import { useLocation } from 'react-router-dom'

import Loader from 'authorization-module/lib/ui/atoms/Loader'
import {
  AllAccountSummaryGetResponse,
  useAllAccountSummary,
  useCreditAccounts
} from 'const/syfApiQueries'
import getIvaUserMetadata from 'helpers/getIvaUserMetadata'
import 'helpers/iconLibrary'

import ROUTES from 'const/routes'
import { useAtom, useAtomValue } from 'jotai'
import { featureFlagsFetchedAtom, selectedAccountAtom } from 'store/atoms'
import { useOnetrustActiveGroups } from 'hooks/useOneTrustConsentModel'
import AppRoutes from './App.routes'
import DashboardLayout from './DashboardLayout'
import DashboardModals from './DashboardModals'
import { useAuthentication } from './hooks/useAuthentication'
import { useLanguage } from './hooks/useLanguage'
import { useNewRelicCustomAttribute } from './hooks/useNewRelicCustomAttribute'
import { useOneTrust } from './hooks/useOneTrust'
import { useQualtricsOnLoad } from './hooks/useQualtricsOnLoad'
import { useDynamicYieldScript } from './hooks/useDynamicYieldScript'
import { useSurflyOneTrustVisibility } from './hooks/useSurflyOneTrustVisibility'
import { useApptimize } from './hooks/useApptimize'
import useDeepLinkNavigation from './hooks/useDeepLinkNavigation'
import usePaymentSecurityDeepLink from './hooks/usePaymentSecurityDeepLink'
import useCustomCardDeepLink from './hooks/useCustomCardDeepLink'

interface QueryResult<T> {
  data: T[]
  isError: boolean
  isFetched: boolean
}

const defaultResult = {
  data: [],
  isError: false,
  isFetched: true
}

/**
 * Main Dashboard component for authenticated user experience.
 * Handles data fetching, authentication, feature flags, and renders the dashboard layout.
 * @returns {ReactElement} The rendered dashboard UI
 */
const Dashboard = (): ReactElement => {
  const selectedAccount = useAtomValue(selectedAccountAtom)
  const [isFeatureFlagsFetched, setFeatureFlagsFetched] = useAtom(
    featureFlagsFetchedAtom
  )
  const location = useLocation()

  useApptimize((fetched: boolean) => {
    setFeatureFlagsFetched(fetched)
  })

  const {
    isTokenFetched,
    setTokenFetched,
    authorizedCallback,
    authenticateCallback
  } = useAuthentication()

  const { handleLanguageChange } = useLanguage()
  useDynamicYieldScript()
  useNewRelicCustomAttribute()
  useQualtricsOnLoad()
  useOnetrustActiveGroups()
  useSurflyOneTrustVisibility()

  const {
    data: { accounts = [], electronicConsent } = {},
    isLoading: isAccountsLoading,
    isError: isCreditAccountsAPIError,
    isFetched: isAccountsFetched
  } = useCreditAccounts()

  useCustomCardDeepLink(
    accounts,
    isAccountsFetched,
    !!isCreditAccountsAPIError,
    isTokenFetched,
    electronicConsent
  )

  const isAccountsDataAvailable = !isAccountsLoading
  const isEconsentModalVisible = !(isAccountsDataAvailable && electronicConsent)

  // Initialize OneTrust cookie consent with relevant flags
  useOneTrust(
    location.pathname,
    isTokenFetched,
    isEconsentModalVisible,
    isFeatureFlagsFetched
  )

  // Get summary data for all accounts, tracking fetched/error state for deep linking
  const {
    data: allAccountSummaryData,
    isError: isAllAccountSummaryError,
    isFetched: isSummaryFetched
  } = useAllAccountSummary(accounts).reduce<
    QueryResult<AllAccountSummaryGetResponse>
  >(
    (accountSummary, { data, isError, isFetched }) => ({
      data: data ? [...accountSummary.data, data] : accountSummary.data,
      isError: accountSummary.isError || isError,
      isFetched: accountSummary.isFetched && isFetched
    }),
    defaultResult
  )
  const isAllAccountSummaryFetched = isAccountsFetched && isSummaryFetched

  useDeepLinkNavigation(
    allAccountSummaryData,
    isAllAccountSummaryFetched,
    isAllAccountSummaryError,
    isTokenFetched
  )

  usePaymentSecurityDeepLink(
    allAccountSummaryData,
    isAllAccountSummaryFetched,
    isTokenFetched
  )

  // Extract IVA user metadata for analytics and personalization
  const ivaUserMetadata = getIvaUserMetadata(allAccountSummaryData)

  // Controls whether dashboard sections and modals are shown
  const [showRunningSections, setShowRunningSections] = useState(
    location.pathname !== ROUTES.HOME
  )

  return (
    <Suspense fallback={<Loader />}>
      <DashboardLayout
        showRunningSections={showRunningSections}
        handleLanguageChange={handleLanguageChange}
        locationPathname={location.pathname}
        selectedAccount={selectedAccount}
        ivaUserMetadata={ivaUserMetadata}
      >
        <AppRoutes
          authenticateCallback={authenticateCallback}
          authorizedCallback={() => {
            authorizedCallback()
            setShowRunningSections(true)
          }}
          setTokenFetched={setTokenFetched}
        />
        {showRunningSections && (
          <DashboardModals
            locationPathname={location.pathname}
            isCreditAccountsAPIError={!!isCreditAccountsAPIError}
          />
        )}
      </DashboardLayout>
    </Suspense>
  )
}

export default Dashboard
